import {
  type PayseraPrepareSignatureMutationVariables,
  usePayseraPrepareSignatureMutation,
} from 'api/core/generated';
import {
  AppApiVersions,
  AppProductType,
  AppSearchParams,
  type CreditLineRoutePaths,
  type HirePurchaseRoutePaths,
  type IncomeInsuranceRoutePaths,
  PAYSERA_PAYMENT_STATUSES,
  type SmallLoanRoutePaths,
} from 'app-constants';
import { useLocation, useSearchParams } from 'react-router-dom';
import {
  getAppRoutePathByProductType,
  replaceOrAddQueryParamsInUrl,
} from 'services';

import { useGetProductTypeByUrl } from './use-get-product-type-by-url';

type SigningExistedProductRoutePaths =
  | typeof HirePurchaseRoutePaths
  | typeof SmallLoanRoutePaths
  | typeof CreditLineRoutePaths
  | typeof IncomeInsuranceRoutePaths
  | null;

export const useSignContractWithBanklink = () => {
  const [searchParams] = useSearchParams();
  const { pathname } = useLocation();

  const productType = useGetProductTypeByUrl();
  const { appRoutePath, productRoutePaths } =
    getAppRoutePathByProductType(productType);

  const [prepareForBanklinkSigning, { data, loading, error }] =
    usePayseraPrepareSignatureMutation({
      context: { apiVersion: AppApiVersions.core },
    });

  const banklinkSigningRedirectUrlParamsByProductTypeMap = {
    [AppProductType.HIRE_PURCHASE]: {
      [AppSearchParams.referenceKey]:
        searchParams.get(AppSearchParams.referenceKey) || '',
    },
    [AppProductType.SMALL_LOAN]: {
      [AppSearchParams.referenceKey]:
        searchParams.get(AppSearchParams.referenceKey) || '',
    },
    [AppProductType.CREDIT_LINE]: {
      [AppSearchParams.creditAccountHash]:
        searchParams.get(AppSearchParams.creditAccountHash) || '',
    },
    [AppProductType.CREDIT_LIMIT_RECALCULATION]: {
      [AppSearchParams.creditLimitRecalculationHash]:
        searchParams.get(AppSearchParams.creditLimitRecalculationHash) || '',
    },
    [AppProductType.CREDIT_LINE_WITHDRAWAL]: {
      [AppSearchParams.creditLineWithdrawalHash]:
        searchParams.get(AppSearchParams.creditLineWithdrawalHash) || '',
    },
    [AppProductType.INCOME_INSURANCE]: {
      [AppSearchParams.insuranceHash]:
        searchParams.get(AppSearchParams.insuranceHash) || '',
    },
  };

  const isSpouseSigning = Boolean(
    pathname.includes(
      (
        productRoutePaths as Exclude<
          SigningExistedProductRoutePaths,
          typeof IncomeInsuranceRoutePaths
        >
      )?.SPOUSE ?? '',
    ),
  );

  const banklinkSigningRedirectUrl = replaceOrAddQueryParamsInUrl({
    returnWithBase: true,
    url: `${window.origin}/${appRoutePath}/${
      isSpouseSigning
        ? (
            productRoutePaths as Exclude<
              SigningExistedProductRoutePaths,
              typeof IncomeInsuranceRoutePaths
            >
          )?.SPOUSE
        : (productRoutePaths as SigningExistedProductRoutePaths)?.SIGNING
    }`,
    params:
      banklinkSigningRedirectUrlParamsByProductTypeMap[
        (productType as Exclude<
          AppProductType,
          | AppProductType.CREDIT_LIMIT_RECALCULATION
          | AppProductType.CREDIT_LINE_WITHDRAWAL
        > | null) ?? AppProductType.CREDIT_LINE
      ],
  });

  const banklinkSigningAcceptUrl = replaceOrAddQueryParamsInUrl({
    returnWithBase: true,
    url: banklinkSigningRedirectUrl,
    params: {
      [AppSearchParams.payseraSigningPaymentStatus]:
        PAYSERA_PAYMENT_STATUSES.successful,
    },
  });

  const banklinkSigningCancelUrl = replaceOrAddQueryParamsInUrl({
    returnWithBase: true,
    url: banklinkSigningRedirectUrl,
    params: {
      [AppSearchParams.payseraSigningPaymentStatus]:
        PAYSERA_PAYMENT_STATUSES.failed,
    },
  });

  const prepareBanklinkContractSignature = (
    variables: PayseraPrepareSignatureMutationVariables,
  ) => prepareForBanklinkSigning({ variables });

  return {
    prepareBanklinkContractSignature,
    banklinkSigningChallengeData: data?.challenge?.redirect_url,
    banklinkSigningProcessing: loading,
    banklinkSigningError: error,
    banklinkSigningAcceptUrl,
    banklinkSigningCancelUrl,
  };
};
